import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, MailWarning, Send, Share2, Loader2, ShieldCheck, Clock, CheckCircle2, XCircle, Inbox, Eye, UserCog, Save, Settings, LogOut } from "lucide-react";

const HELPING = [
  { v: "son", label: "My son" },
  { v: "daughter", label: "My daughter" },
  { v: "family", label: "A family member" },
  { v: "client", label: "A client" },
];
const ROLE_LABEL = { wali: "Wali / Parent", imam: "Imam", registrar: "Islamic Marriage Registrar / Officiant", officiant: "Islamic Marriage Registrar / Officiant" };
const statusBadge = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", declined: "bg-red-100 text-red-700" };

function RequestDialog({ open, onOpenChange, profile, onDone }) {
  const [reason, setReason] = useState("");
  const [helpingWho, setHelpingWho] = useState("");
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setHelpingWho(""); setIntro(""); } }, [open]);

  const submit = async () => {
    if (!reason.trim()) return toast.error("Please add a reason for your request.");
    if (!helpingWho) return toast.error("Please select who you're helping.");
    setBusy(true);
    try {
      await api.post("/assist/request-contact", { targetId: profile.id, reason: reason.trim(), helpingWho, intro: intro.trim() || undefined });
      toast.success("Request sent — the member will be asked to approve.");
      onOpenChange(false); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!profile) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="request-contact-dialog">
        <DialogHeader><DialogTitle>Request contact — {profile.displayName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Reason for requesting contact *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. I'm helping arrange a suitable match and would like to discuss further." data-testid="rc-reason" />
          </div>
          <div className="space-y-1.5">
            <Label>Who are you helping? *</Label>
            <div className="grid grid-cols-2 gap-2">
              {HELPING.map((h) => (
                <button type="button" key={h.v} onClick={() => setHelpingWho(h.v)} data-testid={`rc-helping-${h.v}`}
                  className={`rounded-xl border-2 py-2 text-sm font-medium transition-colors ${helpingWho === h.v ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-900/15 text-emerald-900 hover:bg-mint"}`}>{h.label}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Short introduction (optional)</Label>
            <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Assalamu alaikum, a little about yourself…" data-testid="rc-intro" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="rc-submit" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Send request</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileCard({ p, shareConfig, onRequest }) {
  const navigate = useNavigate();
  const photo = p.photoBase64 || (p.photos && p.photos[0]);
  const loc = [p.city, p.country].filter(Boolean).join(", ");
  const share = async () => {
    const link = shareConfig?.universalLink || "https://nikah-match.com";
    const msg = `I found a profile on Nikah Match that may be a good match: ${p.displayName}${p.age ? `, ${p.age}` : ""}${loc ? ` (${loc})` : ""}. Join Nikah Match to view and connect: ${shareConfig?.downloadUrl || link}`;
    try { if (navigator.share) await navigator.share({ title: "Nikah Match", text: msg, url: shareConfig?.downloadUrl || link }); else { await navigator.clipboard.writeText(msg); toast.success("Invite copied to clipboard"); } } catch {}
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-emerald-900/10 bg-white" data-testid={`assist-profile-${p.id}`}>
      <div className="flex gap-4 p-4">
        <button onClick={() => navigate(`/profiles/${p.id}`)} className="shrink-0" data-testid={`assist-view-avatar-${p.id}`}>
          <Avatar className="h-20 w-20 rounded-2xl border border-amber-500/30">
            {photo && <AvatarImage src={photo} alt="" className="object-cover" />}
            <AvatarFallback className="rounded-2xl bg-emerald-800 text-amber-200">{(p.displayName || "?")[0]}</AvatarFallback>
          </Avatar>
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-lg font-bold text-emerald-900">{p.displayName}{p.age ? `, ${p.age}` : ""}</p>
          <p className="text-xs font-medium uppercase tracking-wide text-amber-600">{p.gender === "female" ? "Sister" : "Brother"}</p>
          {loc && <p className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {loc}</p>}
          {p.profession && <p className="text-sm text-slate-600">{p.profession}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs text-emerald-800/80">
            {[p.sect, p.maritalStatus].filter(Boolean).map((t, i) => <span key={i} className="rounded-full bg-ivory px-2 py-0.5 ring-1 ring-emerald-900/10">{t}</span>)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 border-t border-emerald-900/10 p-3">
        <Button size="sm" variant="outline" onClick={() => navigate(`/profiles/${p.id}`)} data-testid={`view-profile-${p.id}`} className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint"><Eye className="mr-1 h-4 w-4" /> View</Button>
        <Button size="sm" onClick={() => onRequest(p)} data-testid={`request-contact-${p.id}`} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Send className="mr-1 h-4 w-4" /> Request</Button>
        <Button size="sm" variant="outline" onClick={share} data-testid={`share-profile-${p.id}`} className="rounded-full border-emerald-800/30 text-emerald-900 hover:bg-mint"><Share2 className="mr-1 h-4 w-4" /> Share</Button>
      </div>
    </div>
  );
}

function AssistantProfileDialog({ open, onOpenChange, user, onSaved }) {
  const [form, setForm] = useState({ displayName: "", city: "", country: "", organization: "", bio: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const p = user?.profile || {};
      setForm({ displayName: p.name || "", city: p.city || "", country: p.country || "", organization: p.organization || "", bio: p.bio || "" });
    }
  }, [open, user]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", { displayName: form.displayName.trim(), city: form.city.trim(), country: form.country.trim(), organization: form.organization.trim(), bio: form.bio.trim() });
      toast.success("Your assistant profile has been saved.");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assistant-profile-dialog">
        <DialogHeader><DialogTitle>My assistant profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="rounded-xl bg-mint/50 p-3 text-xs text-emerald-900/80">Members who receive your contact requests can see these details. Your profile is never shown in marriage-seeker discovery.</p>
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.displayName} onChange={set("displayName")} data-testid="ap-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} data-testid="ap-city" /></div>
            <div className="space-y-1.5"><Label>Country</Label><Input value={form.country} onChange={set("country")} data-testid="ap-country" /></div>
          </div>
          <div className="space-y-1.5"><Label>Organization / mosque affiliation</Label><Input value={form.organization} onChange={set("organization")} placeholder="e.g. Masjid Al-Noor, London" data-testid="ap-org" /></div>
          <div className="space-y-1.5"><Label>About you</Label><Textarea value={form.bio} onChange={set("bio")} placeholder="A short introduction about your role and how you help." data-testid="ap-bio" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="ap-save" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AssistBrowse() {
  const { user, refreshUser, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/"); };
  const [tab, setTab] = useState("browse");
  const [gender, setGender] = useState("");
  const [profiles, setProfiles] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [shareConfig, setShareConfig] = useState(null);
  const [requests, setRequests] = useState(null);
  const [dialog, setDialog] = useState({ open: false, profile: null });
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => { api.get("/share-config").then(({ data }) => setShareConfig(data)).catch(() => {}); }, []);

  const loadBrowse = useCallback(async (nextPage = 1) => {
    try {
      const { data } = await api.get("/assist/browse", { params: { gender: gender || undefined, page: nextPage, limit: 12 } });
      const list = (data.profiles || []).filter((p) => !gender || p.gender === gender);
      setProfiles((prev) => (nextPage === 1 ? list : [...(prev || []), ...list]));
      setHasMore(!!data.hasMore); setPage(data.page || nextPage); setNeedVerify(false);
    } catch (err) {
      if ((err.response?.data?.detail || "").toLowerCase().includes("verify")) setNeedVerify(true);
      else toast.error(formatApiError(err.response?.data?.detail));
      setProfiles([]);
    }
  }, [gender]);
  const loadRequests = useCallback(async () => {
    try { const { data } = await api.get("/assist/my-contact-requests"); setRequests(data.requests || []); }
    catch (err) { if ((err.response?.data?.detail || "").toLowerCase().includes("verify")) setNeedVerify(true); setRequests([]); }
  }, []);

  useEffect(() => { if (tab === "browse") { setProfiles(null); loadBrowse(1); } else loadRequests(); }, [tab, loadBrowse, loadRequests]);

  return (
    <div className="min-h-screen bg-ivory">
      <header className="sticky top-0 z-20 border-b border-emerald-900/10 bg-ivory/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/"><Logo size="md" /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">{ROLE_LABEL[user?.assistant_role] || "Assistant"} · {user?.profile?.name}</span>
            <Button variant="outline" onClick={() => setProfileOpen(true)} data-testid="assist-my-profile-btn" className="rounded-full border-emerald-800/30 text-emerald-900"><UserCog className="mr-1.5 h-4 w-4" /> My profile</Button>
            <Link to="/assisted"><Button variant="outline" className="rounded-full border-emerald-800/30 text-emerald-900">Linked members</Button></Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="assist-avatar-btn"><Avatar className="h-9 w-9 border border-amber-500/40">{user?.profile?.photo && <AvatarImage src={user.profile.photo} alt="" />}<AvatarFallback className="bg-emerald-800 text-sm text-amber-200">{(user?.profile?.name || "A")[0]?.toUpperCase()}</AvatarFallback></Avatar></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm"><p className="font-semibold text-emerald-900">{user?.profile?.name}</p><p className="truncate text-xs text-slate-500">{user?.email}</p></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="assist-menu-editprofile"><UserCog className="mr-2 h-4 w-4" /> Edit my profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")} data-testid="assist-menu-account"><Settings className="mr-2 h-4 w-4" /> Account &amp; security</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="text-red-600" data-testid="assist-menu-logout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-2 flex items-start gap-3 rounded-2xl bg-mint/50 p-4" data-testid="assist-intro">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
          <div>
            <h1 className="font-serif text-2xl font-bold text-emerald-950">Browse eligible profiles</h1>
            <p className="text-sm text-slate-600">As a {ROLE_LABEL[user?.assistant_role] || "guardian"}, you can review brothers' and sisters' profiles and request contact on behalf of who you're helping. You never appear as a prospective match.</p>
          </div>
        </div>

        {needVerify ? (
          <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-6 text-center" data-testid="assist-verify-gate">
            <MailWarning className="mx-auto mb-2 h-8 w-8 text-amber-600" />
            <p className="font-semibold text-amber-900">Please verify your email to start browsing</p>
            <p className="mt-1 text-sm text-amber-800/90">We've sent a verification link to {user?.email}. Verify it, then refresh this page.</p>
          </div>
        ) : (
          <>
            <div className="mt-5 flex items-center gap-2">
              {[["browse", "Browse profiles"], ["requests", "My contact requests"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`assist-tab-${k}`} className={`rounded-full px-4 py-2 text-sm font-medium ${tab === k ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{l}</button>
              ))}
            </div>

            {tab === "browse" ? (
              <>
                <div className="mt-4 flex gap-2" data-testid="assist-gender-filter">
                  {[["", "Everyone"], ["male", "Brothers"], ["female", "Sisters"]].map(([v, l]) => (
                    <button key={v} onClick={() => setGender(v)} data-testid={`assist-filter-${v || "all"}`} className={`rounded-full px-4 py-2 text-sm font-medium ${gender === v ? "bg-emerald-700 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{l}</button>
                  ))}
                </div>
                {profiles === null ? (
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>
                ) : profiles.length === 0 ? (
                  <p className="mt-10 text-center text-slate-500" data-testid="assist-browse-empty">No profiles to show right now.</p>
                ) : (
                  <>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="assist-browse-grid">
                      {profiles.map((p) => <ProfileCard key={p.id} p={p} shareConfig={shareConfig} onRequest={(prof) => setDialog({ open: true, profile: prof })} />)}
                    </div>
                    {hasMore && <div className="mt-5 text-center"><Button onClick={() => loadBrowse(page + 1)} variant="outline" data-testid="assist-load-more" className="rounded-full border-emerald-800/30 text-emerald-900">Load more</Button></div>}
                  </>
                )}
              </>
            ) : (
              <div className="mt-5 space-y-3" data-testid="assist-requests-list">
                {requests === null ? [...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)
                  : requests.length === 0 ? <p className="py-10 text-center text-slate-500" data-testid="assist-requests-empty"><Inbox className="mx-auto mb-2 h-7 w-7 text-slate-400" />You haven't requested any contacts yet.</p>
                  : requests.map((r) => (
                    <div key={r.requestId} className="flex items-center justify-between rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`my-request-${r.requestId}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-emerald-900">{r.profile?.displayName || "Member"}</p>
                        <p className="truncate text-sm text-slate-500">{r.reason}</p>
                        <p className="text-xs text-slate-400">Helping: {HELPING.find((h) => h.v === r.helpingWho)?.label || r.helpingWho}</p>
                      </div>
                      <span className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-bold uppercase ${statusBadge[r.status] || "bg-slate-100 text-slate-600"}`}>
                        {r.status === "approved" ? <CheckCircle2 className="h-3.5 w-3.5" /> : r.status === "declined" ? <XCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />} {r.status}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </main>

      <RequestDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} profile={dialog.profile} onDone={loadRequests} />
      <AssistantProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onSaved={refreshUser} />
    </div>
  );
}
